import { Box, Button, Typography } from '@mui/material';
import type React from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppDialog from '../components/AppDialog';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeContext';
import { useThemeColors } from '../hooks/useThemeColors';
import { IMG } from '../theme/assets';

/** 窓口ごとの使い方動画。撮れたものから src を入れる（#149） */
interface MethodVideo {
  /** 動画のURL。public/assets/manual/ 配下に置く想定 */
  src: string;
  /** 表紙。無ければ最初のコマが出る */
  poster?: string;
  /** 画面に出す尺。"1:20" のような表記 */
  duration: string;
}

interface MethodCard {
  key: string;
  owl: string;
  title: string;
  desc: string;
  btn: string;
  /** ボタンの色 */
  color: string;
  /** 動画がまだ無いときの、絵を置く面の色 */
  tint: string;
  /** 動画があるときの、再生面の色 */
  screen: string;
  path: string;
  video: MethodVideo | null;
}

/**
 * 「3つの方法でファクトチェックを始める」。
 *
 * 各窓口は上に16:9の面を持つ。そこに入るのはその機能の使い方動画で、
 * **まだ撮れていない**（#149 で中断中）。video が null のあいだは
 * 再生ボタンを出さず、ふくろうの絵を大きく置いた見出し面として振る舞う。
 * 撮れたものから video を入れれば、その窓口だけ再生面に変わる。
 */
const METHOD_CARDS: MethodCard[] = [
  {
    key: 'doc',
    owl: IMG.owl.methodDocument,
    title: '文書のファクトチェック',
    desc: 'PDF・Word・Excel・PowerPoint から主張を抜き出して、一つずつ確かめます。',
    btn: '始める',
    color: '#3b5bdb',
    tint: 'linear-gradient(150deg, #eef4ff, #e4ecfb)',
    screen: 'linear-gradient(150deg, #2b3550, #1a2133)',
    path: '/create',
    video: null,
  },
  {
    key: 'vid',
    owl: IMG.owl.methodVideo,
    title: '動画のファクトチェック',
    desc: '話した内容を文字に起こし、発言ごとにタイムコードを付けて確かめます。',
    btn: '始める',
    color: '#0d9488',
    tint: 'linear-gradient(150deg, #eef9f6, #e1f2ed)',
    screen: 'linear-gradient(150deg, #1f4440, #142a2a)',
    path: '/create-video',
    video: null,
  },
  {
    key: 'dom',
    owl: IMG.owl.methodDomain,
    title: 'サイトを限定してファクトチェック',
    desc: '信頼できるサイトだけに絞って突き合わせます。絞るほど根拠は確かになります。',
    btn: '始める',
    color: '#e0890c',
    tint: 'linear-gradient(150deg, #fff6ea, #fdecd6)',
    screen: 'linear-gradient(150deg, #4a3620, #2b1f14)',
    path: '/domain-factcheck',
    video: null,
  },
];

/**
 * ホームの出現は「背景 → 器 → 主役」の順に、間を置いて立ち上げる。
 *
 * 参考にした ex-avatar は、空→山→陽で場を作ってから器（サイドバー）を出し、
 * 1秒の“ため”を置いて主役の入力欄を最後に置いていた。順番そのものより、
 * 段と段のあいだを 0.2〜1.0 秒空けていることが「ふわっと」の正体だった。
 * ただし向こうは全体で4.5秒。開幕の儀がある画面ならそれでいいが、
 * MIKIWAME は毎日開く道具なので、構造だけ借りて 1.45 秒に畳む。
 *
 * 段ごとに動きを変えるのも向こうに倣う。atmosphere は fade、文字は snap、
 * 見出しは rise、主役の窓口だけ scale を足した box にして、質の違いで段を分ける。
 * イージングは easeOutExpo cubic-bezier(.16, 1, .3, 1) で全段そろえる。
 *
 *   0.00s 背景の霞   WASH 1.1s
 *   0.04s ふくろう    POP  0.50s
 *   0.20s 挨拶       SNAP 0.44s
 *   0.29s 小見出し    SNAP 0.44s
 *   0.44s 節の見出し  RISE 0.56s
 *   0.60s 窓口3枚    BOX  0.66s（0.10s ずつずらす）→ 1.46s で止まる
 */

/**
 * 挨拶は entrance/snap-in（X-24・easeOutExpo）。
 * 短い文なので、長い出現より「パッと出て止まる」ほうが据わりがいい。
 */
const SNAP = {
  '@keyframes mikiwameSnap': {
    from: { opacity: 0, transform: 'translateX(-24px)' },
    to: { opacity: 1, transform: 'translateX(0)' },
  },
  animation: 'mikiwameSnap 0.44s cubic-bezier(0.16, 1, 0.3, 1) both',
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    opacity: 1,
    transform: 'none',
  },
} as const;

/** ふくろうの入れ物。principles/exaggeration（0.84 → 1.0 を back-out で）。 */
const POP = {
  '@keyframes mikiwamePop': {
    from: { opacity: 0, transform: 'scale(0.84)' },
    to: { opacity: 1, transform: 'scale(1)' },
  },
  animation: 'mikiwamePop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both',
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    opacity: 1,
    transform: 'none',
  },
} as const;

/**
 * ふくろうだけは出たあとも止めない。
 * principles/ambient-hold（止まって見えるコマを作らない）。
 */
const BREATHE = {
  '@keyframes mikiwameBreathe': {
    '0%, 100%': { transform: 'translateY(0) rotate(0deg)' },
    '50%': { transform: 'translateY(-5px) rotate(-1.5deg)' },
  },
  animation: 'mikiwameBreathe 4s cubic-bezier(0.37, 0, 0.63, 1) infinite',
  '@media (prefers-reduced-motion: reduce)': { animation: 'none', transform: 'none' },
} as const;

/**
 * 節の見出し。下からふわっと（entrance/soft-rise の Y を控えめにしたもの）。
 * ここは読ませる文字なので、窓口のように縮んだところから来させない。
 */
const RISE = {
  '@keyframes mikiwameRise': {
    from: { opacity: 0, transform: 'translateY(18px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
  animation: 'mikiwameRise 0.56s cubic-bezier(0.16, 1, 0.3, 1) both',
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    opacity: 1,
    transform: 'none',
  },
} as const;

/**
 * 窓口カード専用。rise に scale 0.985 を足しただけだが、
 * わずかに「遠くから寄ってくる」ので、同じ下からでも見出しと別の段に読める。
 * ここがこの画面の主役なので、最後に、いちばん長い尺で置く。
 */
const BOX = {
  '@keyframes mikiwameBox': {
    from: { opacity: 0, transform: 'translateY(20px) scale(0.985)' },
    to: { opacity: 1, transform: 'translateY(0) scale(1)' },
  },
  animation: 'mikiwameBox 0.66s cubic-bezier(0.16, 1, 0.3, 1) both',
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    opacity: 1,
    transform: 'none',
  },
} as const;

/**
 * いちばん先に立ち上がる霞。何も描かれていない画面に「場」を作る役で、
 * 置いてあること自体には気づかれなくていい（だから薄い）。
 * 広がりながら濃くなるので、あとから来る文字や窓口が奥から出たように見える。
 */
const WASH = {
  '@keyframes mikiwameWash': {
    from: { opacity: 0, transform: 'scale(1.06)' },
    to: { opacity: 1, transform: 'scale(1)' },
  },
  animation: 'mikiwameWash 1.1s cubic-bezier(0.16, 1, 0.3, 1) both',
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    opacity: 1,
    transform: 'none',
  },
} as const;

/** 窓口だけの画面なので、ホームの引き（zoom）は掛けない。等倍で大きく見せる。 */
const homeHeight = 'calc(var(--app-vh) - 64px - 64px)';

/**
 * カーソルを載せた1枚だけ、無音でループ再生する。
 *
 * 3枚とも回し続けると同時に3本デコードすることになり重く、視線も定まらない。
 * 触れた1本だけなら読み込みも1本で済む（preload="none"）。
 * マウスが無い端末では効かないので、その場合は表紙のまま。
 */
const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const MyPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { themeMode } = useThemeMode();
  const tokens = useThemeColors();

  /** 再生中の窓口。null なら閉じている */
  const [playing, setPlaying] = useState<MethodCard | null>(null);
  /** カーソルが載っている窓口。そこだけ無音プレビューが回る */
  const [hovered, setHovered] = useState<string | null>(null);

  const textPrimary = themeMode === 'dark' ? '#fff' : '#1a1a2e';
  const textMuted = themeMode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)';
  const dark = themeMode === 'dark';

  /** 1本でも撮れていれば、見出しを動画に触れたものに変える */
  const hasAnyVideo = METHOD_CARDS.some((c) => c.video);

  return (
    <Box
      sx={{
        px: 4,
        minHeight: homeHeight, // AppBar(mt:8=64px) + Layout padding(p:4=32px*2=64px) を引く
        bgcolor: 'transparent', // 背景はLayout側に任せる
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        overflowX: 'hidden',
        position: 'relative',
      }}
    >
      {/* 場を作る霞。いちばん先に広がりながら濃くなる（WASH の説明を参照）。
          クリックは全部下へ抜く。 */}
      <Box
        aria-hidden
        sx={{
          ...WASH,
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 0,
          background: dark
            ? 'radial-gradient(58% 48% at 24% 24%, rgba(45,212,191,0.10), rgba(129,140,248,0.07) 46%, transparent 72%)'
            : 'radial-gradient(58% 48% at 24% 24%, rgba(45,212,191,0.13), rgba(129,140,248,0.10) 46%, transparent 72%)',
        }}
      />

      <Box sx={{ maxWidth: 1400, mx: 'auto', width: '100%', position: 'relative', zIndex: 1 }}>
        {/* ヒーロー（C案: 挨拶と小さなふくろう）。
            帯と長い説明文をやめ、誰に向けた画面かだけを置く。
            説明は下の窓口カードが持っているので、ここで重ねない。 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.25, mb: 4.5 }}>
          <Box
            sx={{
              ...POP,
              animationDelay: '0.04s',
              width: 68,
              height: 68,
              flexShrink: 0,
              borderRadius: '20px',
              background: dark
                ? 'linear-gradient(140deg, rgba(45,212,191,0.16), rgba(129,140,248,0.16))'
                : 'linear-gradient(140deg, #e7f6f1, #eef0fb)',
              display: 'grid',
              placeItems: 'center',
              boxShadow: '0 8px 20px rgba(60,60,140,0.12)',
            }}
          >
            <Box
              component="img"
              src={IMG.owl.hero}
              alt=""
              sx={{ ...BREATHE, width: 54, height: 54, objectFit: 'contain' }}
            />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                ...SNAP,
                animationDelay: '0.2s',
                fontSize: 24,
                fontWeight: 900,
                letterSpacing: '-0.01em',
                lineHeight: 1.25,
                color: textPrimary,
              }}
            >
              こんにちは{user?.displayName ? `、${user.displayName}さん` : ''}
            </Typography>
            <Typography
              sx={{
                ...SNAP,
                animationDelay: '0.29s',
                mt: 0.6,
                fontSize: 14,
                fontWeight: 700,
                color: textMuted,
              }}
            >
              きょうは何を裏取りしますか
            </Typography>
          </Box>
        </Box>

        {/* 検証導線セクション。動画が入ったら見出しもそれに触れる */}
        <Box sx={{ ...RISE, animationDelay: '0.44s', mb: 2.5 }}>
          <Typography sx={{ fontSize: 21, fontWeight: 900, color: textPrimary, lineHeight: 1.2 }}>
            {hasAnyVideo
              ? 'まず1分、どんなことができるか観てください'
              : '3つの方法でファクトチェックを始める'}
          </Typography>
          <Typography sx={{ fontSize: 14, color: textMuted, mt: 0.5 }}>
            {hasAnyVideo ? '観てから、その場で始められます' : '用途に合わせて選んでください'}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 2.75,
          }}
        >
          {METHOD_CARDS.map((card, i) => (
            /* 出現とホバーは別の箱に分ける。同じ箱に載せると、fill-mode: both で
               残る transform が ":hover" の transform を打ち消してしまい、
               浮き上がりが効かなくなる（アニメーションは通常の宣言より強い）。 */
            <Box
              key={card.key}
              sx={{ ...BOX, animationDelay: `${0.6 + i * 0.1}s`, display: 'flex' }}
            >
              <Box
                onMouseEnter={() => setHovered(card.key)}
                onMouseLeave={() => setHovered((k) => (k === card.key ? null : k))}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: '18px',
                  overflow: 'hidden',
                  border: `1px solid ${tokens.line}`,
                  bgcolor: dark ? 'rgba(255,255,255,0.04)' : '#fff',
                  boxShadow: '0 12px 32px rgba(30,30,70,0.09)',
                  transition: 'transform .22s ease, box-shadow .22s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 20px 42px rgba(30,30,70,0.16)',
                  },
                }}
              >
                {/* 上: 16:9。動画があれば再生面、無ければ絵の面 */}
                <Box
                  component={card.video ? 'button' : 'div'}
                  type={card.video ? 'button' : undefined}
                  aria-label={card.video ? `${card.title}の使い方を再生` : undefined}
                  onClick={card.video ? () => setPlaying(card) : undefined}
                  sx={{
                    position: 'relative',
                    width: '100%',
                    aspectRatio: '16 / 9',
                    p: 0,
                    border: 'none',
                    display: 'grid',
                    placeItems: 'center',
                    overflow: 'hidden',
                    background: card.video ? card.screen : card.tint,
                    cursor: card.video ? 'pointer' : 'default',
                  }}
                >
                  <Box
                    component="img"
                    src={card.owl}
                    alt=""
                    sx={
                      card.video
                        ? {
                            position: 'absolute',
                            right: 8,
                            bottom: 4,
                            width: 92,
                            height: 92,
                            objectFit: 'contain',
                            opacity: 0.4,
                          }
                        : {
                            width: 132,
                            height: 132,
                            objectFit: 'contain',
                            filter: 'drop-shadow(0 10px 16px rgba(30,30,70,0.14))',
                          }
                    }
                  />

                  {/* カーソルが載っているあいだだけ、無音で頭から回す。
                    音は大きいプレイヤーを開いたときに初めて出る。 */}
                  {card.video && hovered === card.key && !prefersReducedMotion() && (
                    <Box
                      component="video"
                      src={card.video.src}
                      poster={card.video.poster}
                      muted
                      loop
                      playsInline
                      autoPlay
                      preload="none"
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  )}

                  {card.video && (
                    <>
                      <Box
                        sx={{
                          position: 'relative',
                          width: 56,
                          height: 56,
                          borderRadius: '50%',
                          bgcolor: 'rgba(255,255,255,0.95)',
                          display: 'grid',
                          placeItems: 'center',
                          boxShadow: '0 8px 20px rgba(0,0,0,0.3)',
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            width: 0,
                            height: 0,
                            ml: 0.5,
                            borderTop: '9px solid transparent',
                            borderBottom: '9px solid transparent',
                            borderLeft: '15px solid #1b2030',
                          }}
                        />
                      </Box>
                      <Typography
                        sx={{
                          position: 'absolute',
                          right: 12,
                          top: 11,
                          fontSize: 11,
                          fontWeight: 800,
                          color: '#fff',
                          bgcolor: 'rgba(0,0,0,0.42)',
                          borderRadius: 999,
                          px: 1.1,
                          py: 0.25,
                        }}
                      >
                        {card.video.duration}
                      </Typography>
                    </>
                  )}
                </Box>

                {/* 下: 中身と「始める」 */}
                <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 900,
                      lineHeight: 1.3,
                      color: dark ? tokens.ink : '#1b2030',
                    }}
                  >
                    {card.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 12.5,
                      lineHeight: 1.55,
                      color: dark ? tokens.muted : '#5c6275',
                      mt: 0.9,
                      mb: 2,
                    }}
                  >
                    {card.desc}
                  </Typography>
                  <Button
                    disableElevation
                    variant="contained"
                    onClick={() => navigate(card.path)}
                    endIcon={
                      <Box component="span" sx={{ fontSize: 16, lineHeight: 1, ml: -0.25 }}>
                        →
                      </Box>
                    }
                    sx={{
                      mt: 'auto',
                      alignSelf: 'flex-start',
                      background: card.color,
                      color: '#fff',
                      fontWeight: 800,
                      fontSize: 13.5,
                      borderRadius: '11px',
                      px: 2,
                      py: 1,
                      textTransform: 'none',
                      boxShadow: 'none',
                      '&:hover': {
                        background: card.color,
                        filter: 'brightness(0.95)',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    {card.btn}
                  </Button>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>

        {/* 検証したものは左メニューの「マイアイテム」へ移したので、その行き先を添える */}
        <Box sx={{ ...RISE, animationDelay: '0.95s', mt: 3.5, textAlign: 'center' }}>
          <Button
            onClick={() => navigate('/items')}
            sx={{
              textTransform: 'none',
              fontSize: 13.5,
              fontWeight: 800,
              color: tokens.muted,
              '&:hover': { background: 'transparent', color: tokens.teal },
            }}
          >
            これまでに検証したものを見る →
          </Button>
        </Box>
      </Box>

      {/* 使い方動画。再生面を押したときだけ開く */}
      <AppDialog
        open={Boolean(playing)}
        onClose={() => setPlaying(null)}
        size="lg"
        title={playing ? `${playing.title}の使い方` : ''}
        subtitle={playing?.video ? `${playing.video.duration}` : undefined}
        icon={
          playing ? (
            <Box
              component="img"
              src={playing.owl}
              alt=""
              sx={{ width: 34, height: 34, objectFit: 'contain' }}
            />
          ) : undefined
        }
      >
        {playing?.video && (
          <Box
            component="video"
            src={playing.video.src}
            poster={playing.video.poster}
            controls
            autoPlay
            sx={{
              width: '100%',
              aspectRatio: '16 / 9',
              display: 'block',
              borderRadius: '12px',
              bgcolor: '#000',
            }}
          />
        )}
      </AppDialog>
    </Box>
  );
};

export default MyPage;
